import type { MemberOrdersResponse } from './myOrdersLogic'
import { clearLiffLogin, hasFreshLiffLogin } from '../lib/liffSession'

const ACTIVE_BOT_KEY = 'oc_member_active_bot_v1'

const SESSION_PREFIX = 'oc_member_session_v1:'
const CACHE_PREFIX = 'oc_my_orders_v2'
const CACHE_TTL_MS = 15 * 60 * 1000

export interface MemberSession {
  idToken: string
  userId: string
  expiresAt: number
}

export function tokenSession(idToken: string, botId: string, now = Date.now()): MemberSession | null {
  try {
    const encoded = idToken.split('.')[1]
    const payload = JSON.parse(atob(encoded.replace(/-/g, '+').replace(/_/g, '/'))) as { sub?: string; aud?: string; exp?: number }
    const channel = botId === '2' ? '2009352737' : '2008655508'
    const expiresAt = Number(payload.exp) * 1000
    if (!payload.sub || payload.aud !== channel || !Number.isFinite(expiresAt) || expiresAt <= now + 30_000) return null
    return { idToken, userId: payload.sub, expiresAt }
  } catch {
    return null
  }
}

/** ใช้เฉพาะ token ที่ backend เคยยืนยันแล้วในแท็บนี้; OAuth รอบใหม่ต้องตรวจ LINE ก่อน */
export function readMemberSession(botId: string, search: string): MemberSession | null {
  try {
    const params = new URLSearchParams(search)
    if (['code', 'state', 'error', 'liff.state', 'access_token', 'id_token'].some((key) => params.has(key))) return null
    if (!hasFreshLiffLogin(botId)) return null
    const raw = sessionStorage.getItem(SESSION_PREFIX + botId)
    if (!raw) return null
    const saved = JSON.parse(raw) as MemberSession
    return tokenSession(saved.idToken, botId)
  } catch {
    return null
  }
}

export function saveMemberSession(idToken: string, botId: string): void {
  const session = tokenSession(idToken, botId)
  if (!session) return
  try { sessionStorage.setItem(SESSION_PREFIX + botId, JSON.stringify(session)) } catch { /* private mode */ }
}

export function clearMemberSession(botId: string): void {
  try { sessionStorage.removeItem(SESSION_PREFIX + botId) } catch { /* private mode */ }
}

export function memberCacheKeyForUser(userId: string, botId: string, asOrderId: string | null): string {
  return `${CACHE_PREFIX}:${botId}:${asOrderId || 'me'}:line:${userId}`
}

export function readMemberCache(key: string): MemberOrdersResponse | null {
  try {
    // ล้างข้อมูลส่วนตัวจากแคชถาวรรุ่นเดิม ไม่ย้ายกลับเข้ามาใช้งาน
    localStorage.removeItem(key)
    const raw = sessionStorage.getItem(key)
    if (!raw) return null
    const cached = JSON.parse(raw) as { at: number; data: MemberOrdersResponse }
    const age = Date.now() - cached.at
    if (!Number.isFinite(age) || age < 0 || age > CACHE_TTL_MS || !cached.data?.ok || !Array.isArray(cached.data.orders)) return null
    return cached.data
  } catch { return null }
}

/**
 * แคชยังใหม่พอจะไม่ยิง memberApi ซ้ำ — listMyOrders อ่านออร์เดอร์ทั้งหมดของลูกค้าทุกครั้ง (Firestore reads)
 * ลูกค้าสลับไปมาระหว่างหน้าออร์เดอร์/แชทภายใน 1 นาทีใช้ข้อมูลเดิมได้ · ปุ่ม "ลองใหม่"/บันทึกโปรไฟล์ยังบังคับดึงสด
 */
export function isMemberCacheFresh(key: string, maxAgeMs: number, now = Date.now()): boolean {
  try {
    const raw = sessionStorage.getItem(key)
    if (!raw) return false
    const age = now - Number((JSON.parse(raw) as { at?: number }).at)
    return Number.isFinite(age) && age >= 0 && age < maxAgeMs
  } catch { return false }
}

/** ไม่แคชป้ายประกาศ/สถานะ "ใหม่" ของถุงทอง — ป้ายที่ลูกค้าปิดไปแล้วต้องไม่เด้งซ้ำจากแคช (ขึ้นเฉพาะข้อมูลสดจาก server) */
export function cacheableMemberData(data: MemberOrdersResponse): MemberOrdersResponse {
  return {
    ...data,
    announcements: [],
    activitiesHasNew: false,
    ...(Array.isArray(data.activities) ? { activities: data.activities.map((a) => ({ ...a, isNew: false })) } : {}),
  }
}

export function writeMemberCache(key: string, data: MemberOrdersResponse): void {
  try {
    localStorage.removeItem(key)
    sessionStorage.setItem(key, JSON.stringify({ at: Date.now(), data: cacheableMemberData(data) }))
  } catch { /* private mode */ }
}

/** Routing hint only; memberApi still verifies the LINE token before returning data. */
export function markMemberLogin(botId: string): void {
  try { localStorage.setItem(ACTIVE_BOT_KEY, botId === '2' ? '2' : '1') } catch { /* private mode */ }
}

export function readActiveMemberBot(preferredBotId = ''): string | null {
  try {
    const active = localStorage.getItem(ACTIVE_BOT_KEY)
    const candidates = preferredBotId ? [preferredBotId] : [active, '1', '2']
    for (const bot of candidates) {
      if (bot !== '1' && bot !== '2' || !hasFreshLiffLogin(bot)) continue
      if (active === bot) return bot
      // Existing sessions created before the routing hint was introduced.
      const raw = sessionStorage.getItem(SESSION_PREFIX + bot)
      if (!raw) continue
      const saved = JSON.parse(raw) as Partial<MemberSession>
      if (saved.userId && saved.idToken && Number.isFinite(saved.expiresAt)) return bot
    }
  } catch { /* private mode */ }
  return null
}

export function clearMemberLogin(): void {
  clearLiffLogin('1')
  clearLiffLogin('2')
  clearMemberSession('1')
  clearMemberSession('2')
  try {
    localStorage.removeItem(ACTIVE_BOT_KEY)
    for (let index = localStorage.length - 1; index >= 0; index -= 1) {
      const key = localStorage.key(index)
      if (key?.startsWith(CACHE_PREFIX + ':')) localStorage.removeItem(key)
    }
    for (let index = sessionStorage.length - 1; index >= 0; index -= 1) {
      const key = sessionStorage.key(index)
      if (key?.startsWith(CACHE_PREFIX + ':') || key?.startsWith('oc_admin_viewer:') || key === 'oc_member_relogin') sessionStorage.removeItem(key)
    }
  } catch { /* private mode */ }
}
